import React, { useEffect, useRef, useState, useCallback } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const useAnimationFrame = (callback) => {
  const requestRef = useRef();
  const previousTimeRef = useRef();

  const animate = useCallback(
    (time) => {
      if (previousTimeRef.current !== undefined) {
        const deltaTime = time - previousTimeRef.current;
        callback(deltaTime);
      }
      previousTimeRef.current = time;
      requestRef.current = requestAnimationFrame(animate);
    },
    [callback]
  );

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, [animate]);
};

const useMousePosition = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return mousePosition;
};

const vertexShaderSource = `
  attribute vec4 a_position;
  attribute vec3 a_normal;
  uniform mat4 u_matrix;
  uniform mat4 u_normalMatrix;
  varying vec3 v_normal;
  varying vec3 v_position;

  void main() {
    gl_Position = u_matrix * a_position;
    v_normal = mat3(u_normalMatrix) * a_normal;
    v_position = a_position.xyz;
  }
`;

const fragmentShaderSource = `
  precision mediump float;
  varying vec3 v_normal;
  varying vec3 v_position;
  uniform vec3 u_lightDirection;
  uniform vec3 u_color;
  uniform float u_time;

  void main() {
    vec3 normal = normalize(v_normal);
    vec3 lightDir = normalize(u_lightDirection);
    float light = clamp(dot(normal, lightDir), 0.0, 1.0);

    vec3 ambient = u_color * 0.3;
    vec3 diffuse = u_color * light * 0.7;
    vec3 color = ambient + diffuse;

    float sin1 = sin(u_time * 0.0001 + v_position.x * 0.5);
    float sin2 = sin(u_time * 0.0002 + v_position.y * 0.5);
    float sin3 = sin(u_time * 0.0001 + v_normal.x * 0.5);
    vec3 sinus = vec3(sin1, sin2, sin3) * 0.12;

    color += sinus;
    gl_FragColor = vec4(color, 1.0);
  }
`;

const mat4 = {
  create: () => new Float32Array(16),
  identity: (out) => {
    out[0] = 1;
    out[5] = 1;
    out[10] = 1;
    out[15] = 1;
    out[1] = out[2] = out[3] = out[4] = out[6] = out[7] = out[8] = out[9] = out[11] = out[12] = out[13] = out[14] = 0;
    return out;
  },
  perspective: (out, fovy, aspect, near, far) => {
    const f = 1 / Math.tan(fovy / 2);
    const nf = 1 / (near - far);
    out[0] = f / aspect;
    out[1] = 0;
    out[2] = 0;
    out[3] = 0;
    out[4] = 0;
    out[5] = f;
    out[6] = 0;
    out[7] = 0;
    out[8] = 0;
    out[9] = 0;
    out[10] = (far + near) * nf;
    out[11] = -1;
    out[12] = 0;
    out[13] = 0;
    out[14] = 2 * far * near * nf;
    out[15] = 0;
    return out;
  },
  multiply: (out, a, b) => {
    const a00 = a[0], a01 = a[1], a02 = a[2], a03 = a[3];
    const a10 = a[4], a11 = a[5], a12 = a[6], a13 = a[7];
    const a20 = a[8], a21 = a[9], a22 = a[10], a23 = a[11];
    const a30 = a[12], a31 = a[13], a32 = a[14], a33 = a[15];

    let b0 = b[0], b1 = b[1], b2 = b[2], b3 = b[3];
    out[0] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
    out[1] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
    out[2] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
    out[3] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;

    b0 = b[4];
    b1 = b[5];
    b2 = b[6];
    b3 = b[7];
    out[4] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
    out[5] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
    out[6] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
    out[7] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;

    b0 = b[8];
    b1 = b[9];
    b2 = b[10];
    b3 = b[11];
    out[8] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
    out[9] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
    out[10] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
    out[11] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;

    b0 = b[12];
    b1 = b[13];
    b2 = b[14];
    b3 = b[15];
    out[12] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30;
    out[13] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31;
    out[14] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32;
    out[15] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33;
    return out;
  },
  rotateY: (out, a, rad) => {
    const s = Math.sin(rad);
    const c = Math.cos(rad);
    const a00 = a[0], a01 = a[1], a02 = a[2], a03 = a[3];
    const a20 = a[8], a21 = a[9], a22 = a[10], a23 = a[11];

    out[0] = a00 * c - a20 * s;
    out[1] = a01 * c - a21 * s;
    out[2] = a02 * c - a22 * s;
    out[3] = a03 * c - a23 * s;
    out[8] = a00 * s + a20 * c;
    out[9] = a01 * s + a21 * c;
    out[10] = a02 * s + a22 * c;
    out[11] = a03 * s + a23 * c;

    if (a !== out) {
      out[4] = a[4];
      out[5] = a[5];
      out[6] = a[6];
      out[7] = a[7];
      out[12] = a[12];
      out[13] = a[13];
      out[14] = a[14];
      out[15] = a[15];
    }
    return out;
  },
  translate: (out, a, v) => {
    const x = v[0], y = v[1], z = v[2];
    out[12] = a[0] * x + a[4] * y + a[8] * z + a[12];
    out[13] = a[1] * x + a[5] * y + a[9] * z + a[13];
    out[14] = a[2] * x + a[6] * y + a[10] * z + a[14];
    out[15] = a[3] * x + a[7] * y + a[11] * z + a[15];

    if (a !== out) {
      out[0] = a[0];
      out[1] = a[1];
      out[2] = a[2];
      out[3] = a[3];
      out[4] = a[4];
      out[5] = a[5];
      out[6] = a[6];
      out[7] = a[7];
      out[8] = a[8];
      out[9] = a[9];
      out[10] = a[10];
      out[11] = a[11];
    }
    return out;
  },
};

export const ParticleHero = () => {
  const canvasRef = useRef(null);
  const glRef = useRef(null);
  const renderRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const [particles, setParticles] = useState([]);
  const mousePosition = useMousePosition();
  const containerRef = useRef(null);

  useEffect(() => {
    setParticles(
      Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        size: Math.random() * 3 + 3,
        opacity: Math.random() * 0.35 + 0.3,
      }))
    );
  }, []);

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl");
    if (!gl) return;

    const createShader = (type, source) => {
      const shader = gl.createShader(type);
      if (!shader) return null;
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vertexShader = createShader(gl.VERTEX_SHADER, vertexShaderSource);
    const fragmentShader = createShader(gl.FRAGMENT_SHADER, fragmentShaderSource);
    if (!vertexShader || !fragmentShader) return;

    const program = gl.createProgram();
    if (!program) return;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;

    const positions = new Float32Array([
      -1, -1, 1, 1, -1, 1, 1, 1, 1, -1, 1, 1, -1, -1, -1, -1, 1, -1, 1, 1,
      -1, 1, -1, -1, -1, 1, -1, -1, 1, 1, 1, 1, 1, 1, 1, -1, -1, -1, -1, 1,
      -1, 1, -1, 1, -1, 1, -1, 1,
    ]);

    const normals = new Float32Array([
      0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, -1, 0, 0, -1, 0, 0, -1, 0,
      0, -1, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0, 0, -1, 0, 0, -1, 0, 0, -1,
      0, 0, -1, 0,
    ]);

    const indices = new Uint16Array([
      0, 1, 2, 0, 2, 3, 4, 5, 6, 4, 6, 7, 8, 9, 10, 8, 10, 11, 12, 13, 14,
      12, 14, 15, 16, 17, 18, 16, 18, 19, 20, 21, 22, 20, 22, 23,
    ]);

    const positionBuffer = gl.createBuffer();
    const normalBuffer = gl.createBuffer();
    const indexBuffer = gl.createBuffer();
    if (!positionBuffer || !normalBuffer || !indexBuffer) return;

    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, normalBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, normals, gl.STATIC_DRAW);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW);

    renderRef.current = {
      program,
      positionBuffer,
      normalBuffer,
      indexBuffer,
      positionLocation: gl.getAttribLocation(program, "a_position"),
      normalLocation: gl.getAttribLocation(program, "a_normal"),
      matrixLocation: gl.getUniformLocation(program, "u_matrix"),
      normalMatrixLocation: gl.getUniformLocation(program, "u_normalMatrix"),
      lightDirectionLocation: gl.getUniformLocation(program, "u_lightDirection"),
      colorLocation: gl.getUniformLocation(program, "u_color"),
      timeLocation: gl.getUniformLocation(program, "u_time"),
      indexCount: indices.length,
    };

    glRef.current = gl;
    gl.enable(gl.DEPTH_TEST);
    gl.enable(gl.CULL_FACE);

    return () => {
      gl.deleteProgram(program);
      gl.deleteBuffer(positionBuffer);
      gl.deleteBuffer(normalBuffer);
      gl.deleteBuffer(indexBuffer);
    };
  }, []);

  useAnimationFrame(
    useCallback(
      (deltaTime) => {
        setRotation((prev) => prev + deltaTime * 0.001);
        setParticles((prev) =>
          prev.map((particle) => {
            let newX = particle.x + particle.vx;
            let newY = particle.y + particle.vy;
            let newVx = particle.vx;
            let newVy = particle.vy;
            if (newX < 0 || newX > 100) newVx = -newVx;
            if (newY < 0 || newY > 100) newVy = -newVy;
            return {
              ...particle,
              x: Math.max(0, Math.min(100, newX)),
              y: Math.max(0, Math.min(100, newY)),
              vx: newVx,
              vy: newVy,
            };
          })
        );

        const gl = glRef.current;
        const canvas = canvasRef.current;
        const render = renderRef.current;
        if (!gl || !canvas || !render) return;

        const displayWidth = canvas.clientWidth;
        const displayHeight = canvas.clientHeight;
        if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
          canvas.width = displayWidth;
          canvas.height = displayHeight;
          gl.viewport(0, 0, displayWidth, displayHeight);
        }

        gl.clearColor(0.02, 0.03, 0.1, 1.0);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
        gl.useProgram(render.program);

        gl.bindBuffer(gl.ARRAY_BUFFER, render.positionBuffer);
        gl.enableVertexAttribArray(render.positionLocation);
        gl.vertexAttribPointer(render.positionLocation, 3, gl.FLOAT, false, 0, 0);

        gl.bindBuffer(gl.ARRAY_BUFFER, render.normalBuffer);
        gl.enableVertexAttribArray(render.normalLocation);
        gl.vertexAttribPointer(render.normalLocation, 3, gl.FLOAT, false, 0, 0);

        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, render.indexBuffer);

        const projection = mat4.create();
        mat4.perspective(projection, Math.PI / 4, displayWidth / displayHeight, 0.1, 100);

        const view = mat4.create();
        mat4.identity(view);
        mat4.translate(view, view, [0, 0, -4.5]);

        const model = mat4.create();
        mat4.identity(model);
        mat4.rotateY(model, model, rotation);

        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const mouseX = (mousePosition.x - rect.left) / rect.width - 0.5;
          mat4.rotateY(model, model, mouseX * 0.9);
        }

        const mv = mat4.create();
        mat4.multiply(mv, view, model);
        const mvp = mat4.create();
        mat4.multiply(mvp, projection, mv);

        gl.uniformMatrix4fv(render.matrixLocation, false, mvp);
        gl.uniformMatrix4fv(render.normalMatrixLocation, false, model);
        gl.uniform3fv(render.lightDirectionLocation, [0.5, 0.7, 0.2]);
        gl.uniform3fv(render.colorLocation, [0.55, 0.45, 1.0]);
        gl.uniform1f(render.timeLocation, Date.now());

        gl.drawElements(gl.TRIANGLES, render.indexCount, gl.UNSIGNED_SHORT, 0);
      },
      [rotation, mousePosition]
    )
  );

  return (
    <section ref={containerRef} className="advanced-dev-container" data-testid="hero-section">
      <canvas ref={canvasRef} className="hero-canvas" aria-hidden="true" />

      <div className="particles-layer" aria-hidden="true">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="particle"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              opacity: particle.opacity,
            }}
          />
        ))}
      </div>

      <div className="glow-effect" aria-hidden="true" />

      <div className="content-overlay">
        <h1 className="title">Building Intelligent AI Solutions</h1>
        <p className="hero-subtitle">
          Custom AI products, automation systems, and intelligent platforms for startups and enterprises.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
          <Button onClick={() => scrollToSection("#booking")} className="btn-primary">
            Book Free Consultation <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button onClick={() => scrollToSection("#contact")} className="btn-secondary">
            Talk to AI Experts
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ParticleHero;